import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { doc, getDoc } from "firebase/firestore";
import { useAuth } from "./AuthContext";
import { TokenContext } from "./tokenContext";
import { logout, db } from "../utils/firebase";
import GlobalNavDrawer from "./GlobalNavDrawer";
import {
  canManageHiringEvents,
  useJobBankRole
} from "./jobBankRole";

import {
  createHiringEvent,
  deleteHiringEvent,
  getEventRegistrations,
  getHiringEvents,
  getMyEventRegistrations,
  registerForHiringEvent,
  updateEventRegistration,
  updateHiringEvent
} from "../services/eventService";

import "./HiringEventsPortal.css";

const EMPTY_EVENT = {
  title: "",
  eventType: "Employer Mixer",
  startDateTime: "",
  endDateTime: "",
  location: "Spruce Grove, AB",
  description: "",
  targetSectors: "",
  employerCapacity: 25,
  candidateCapacity: 25,
  active: true
};

const EMPTY_REGISTRATION = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  organizationName: "",
  positionTitle: "",
  notes: ""
};

function clean(value) {
  return String(value || "").trim();
}

function formatDateTime(value) {
  if (!value) return "Date not set";
  const date = value?.toDate ? value.toDate() : new Date(value);
  if (isNaN(date.getTime())) return String(value);

  return date.toLocaleString([], {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
}

function splitLines(value) {
  return String(value || "")
    .split("\n")
    .map(item => item.trim())
    .filter(Boolean);
}

function joinLines(value) {
  return Array.isArray(value) ? value.join("\n") : value || "";
}

function formFromEvent(event) {
  return {
    ...EMPTY_EVENT,
    ...event,
    targetSectors: joinLines(event.targetSectors),
    employerCapacity: event.employerCapacity || 25,
    candidateCapacity: event.candidateCapacity || 25,
    active: event.active !== false
  };
}

function getRegistrationName(registration) {
  if (registration.attendeeType === "Employer") {
    return (
      clean(registration.organizationName) ||
      clean(`${registration.firstName || ""} ${registration.lastName || ""}`) ||
      clean(registration.email) ||
      "Unnamed Employer"
    );
  }

  return (
    clean(`${registration.firstName || ""} ${registration.lastName || ""}`) ||
    clean(registration.email) ||
    clean(registration.userId) ||
    "Unnamed Candidate"
  );
}

export default function HiringEventsPortal() {
  const { currentUser } = useAuth();
  const { role, roleLoading } = useJobBankRole(currentUser);
  const { balance, setBalance } = useContext(TokenContext);

  const canManageEvents = canManageHiringEvents(role);

  const [tab, setTab] = useState("events");
  const [attendeeView, setAttendeeView] = useState("Candidate");
  const [events, setEvents] = useState([]);
  const [myRegistrations, setMyRegistrations] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [eventForm, setEventForm] = useState(EMPTY_EVENT);
  const [editingEventId, setEditingEventId] = useState(null);
  const [registrationForm, setRegistrationForm] = useState(EMPTY_REGISTRATION);
  const [attendeeType, setAttendeeType] = useState("Candidate");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingEvent, setSavingEvent] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [savingRegistrationId, setSavingRegistrationId] = useState(null);

  useEffect(() => {
    let isMounted = true;

    async function fetchTokens() {
      if (!currentUser?.uid) return;
      const userRef = doc(db, "users", currentUser.uid);

      try {
        const userDoc = await getDoc(userRef);
        if (userDoc.exists() && isMounted) {
          setBalance(userDoc.data().tokens ?? 0);
        }
      } catch (error) {
        console.error("Error fetching token count:", error);
      }
    }

    fetchTokens();
    return () => {
      isMounted = false;
    };
  }, [currentUser, setBalance]);

  const loadEvents = useCallback(async () => {
    try {
      setLoading(true);

      const [eventResults, registrationResults] = await Promise.all([
        getHiringEvents(true),
        getMyEventRegistrations()
      ]);

      setEvents(eventResults);
      setMyRegistrations(registrationResults);

      setSelectedEvent(prev => {
        if (!eventResults.length) return null;
        return eventResults.find(event => event.id === prev?.id) || eventResults[0];
      });
    } catch (error) {
      console.error("Unable to load hiring events:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const selectEvent = useCallback(async (event) => {
    setSelectedEvent(event);

    if (!event?.id || !canManageEvents) {
      setRegistrations([]);
      return;
    }

    const eventRegistrations = await getEventRegistrations(event.id);
    setRegistrations(eventRegistrations);
  }, [canManageEvents]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  useEffect(() => {
    if (selectedEvent && !roleLoading && canManageEvents) {
      selectEvent(selectedEvent);
    }
  }, [selectedEvent, roleLoading, canManageEvents, selectEvent]);

  const activeEvents = useMemo(() => {
    return events.filter(event => event.active !== false);
  }, [events]);

  const candidateRegistrations = useMemo(() => {
    return registrations.filter(registration => registration.attendeeType === "Candidate");
  }, [registrations]);

  const employerRegistrations = useMemo(() => {
    return registrations.filter(registration => registration.attendeeType === "Employer");
  }, [registrations]);

  const checkedInRegistrations = useMemo(() => {
    return registrations.filter(registration => registration.checkedIn);
  }, [registrations]);

  const filteredRegistrations = useMemo(() => {
    const base =
      attendeeView === "Checked In"
        ? checkedInRegistrations
        : registrations.filter(reg => reg.attendeeType === attendeeView);

    const search = clean(searchTerm).toLowerCase();
    if (!search) return base;

    return base.filter(registration =>
      getRegistrationName(registration).toLowerCase().includes(search) ||
      String(registration.email || "").toLowerCase().includes(search) ||
      String(registration.phone || "").toLowerCase().includes(search) ||
      String(registration.organizationName || "").toLowerCase().includes(search)
    );
  }, [attendeeView, registrations, checkedInRegistrations, searchTerm]);

  const selectedStats = useMemo(() => {
    return {
      total: registrations.length,
      employers: employerRegistrations.length,
      candidates: candidateRegistrations.length,
      checkedIn: checkedInRegistrations.length,
      notCheckedIn: registrations.length - checkedInRegistrations.length
    };
  }, [registrations, employerRegistrations, candidateRegistrations, checkedInRegistrations]);

  function updateForm(field, value) {
    setEventForm(prev => ({ ...prev, [field]: value }));
  }

  function updateRegistrationForm(field, value) {
    setRegistrationForm(prev => ({ ...prev, [field]: value }));
  }

  function editEvent(event) {
    if (!canManageEvents) {
      alert("Only Career Coaches and Employers can edit hiring events.");
      return;
    }

    setEditingEventId(event.id);
    setEventForm(formFromEvent(event));
    setTab("manage");
  }

  function clearEventForm() {
    setEditingEventId(null);
    setEventForm(EMPTY_EVENT);
  }

  async function saveEvent(e) {
    e.preventDefault();

    if (!canManageEvents) {
      alert("Only Career Coaches and Employers can create or update hiring events.");
      return;
    }

    try {
      setSavingEvent(true);

      const payload = {
        ...eventForm,
        employerCapacity: Number(eventForm.employerCapacity || 0),
        candidateCapacity: Number(eventForm.candidateCapacity || 0),
        targetSectors: splitLines(eventForm.targetSectors)
      };

      const result = editingEventId
        ? await updateHiringEvent(editingEventId, payload)
        : await createHiringEvent(payload);

      if (!result.success) {
        alert(result.error || "Unable to save event.");
        return;
      }

      clearEventForm();
      await loadEvents();
      setTab("events");
    } catch (error) {
      console.error(error);
      alert("Unable to save event.");
    } finally {
      setSavingEvent(false);
    }
  }

  async function removeEvent(eventId) {
    if (!canManageEvents) {
      alert("Only Career Coaches and Employers can remove hiring events.");
      return;
    }

    if (!window.confirm("Remove this event?")) return;

    await deleteHiringEvent(eventId);
    setSelectedEvent(null);
    setRegistrations([]);
    await loadEvents();
  }

  async function registerForEvent(event) {
    try {
      setRegistering(true);

      const fallbackProfile = currentUser
        ? {
            email: currentUser.email || "",
            firstName: "",
            lastName: ""
          }
        : {};

      const result = await registerForHiringEvent({
        event,
        attendeeType,
        registrationData: {
          ...fallbackProfile,
          ...registrationForm,
          firstName: clean(registrationForm.firstName),
          lastName: clean(registrationForm.lastName),
          email: clean(registrationForm.email) || currentUser?.email || "",
          userId: currentUser?.uid || ""
        }
      });

      if (!result.success) {
        alert(result.message || result.error || "Unable to register.");
        return;
      }

      setRegistrationForm(EMPTY_REGISTRATION);
      await loadEvents();
      await selectEvent(event);
      alert("Registration submitted.");
    } catch (error) {
      console.error(error);
      alert("Unable to register.");
    } finally {
      setRegistering(false);
    }
  }

  async function toggleCheckIn(registration) {
    if (!canManageEvents) {
      alert("Only Career Coaches and Employers can manage event attendance.");
      return;
    }

    try {
      setSavingRegistrationId(registration.id);

      await updateEventRegistration(registration.id, {
        checkedIn: !registration.checkedIn,
        status: registration.checkedIn ? "Registered" : "Checked In"
      });

      if (selectedEvent) {
        await selectEvent(selectedEvent);
      }
    } finally {
      setSavingRegistrationId(null);
    }
  }

  if (loading) {
    return (
      <div className="hiringEventsPortal">
        <h2>Loading Hiring Events...</h2>
      </div>
    );
  }

  function YourParticipationCard({ selectedEvent, myRegistrations }) {
    const myRegistration = myRegistrations.find(reg =>
      reg.eventId === selectedEvent?.id ||
      reg.eventTitle === selectedEvent?.title
    );

    if (!selectedEvent) return null;

    return (
      <section className="yourParticipationCard">
        <p className="participationEyebrow">
          Your Participation
        </p>

        {!myRegistration ? (
          <>
            <h3>Not Registered</h3>
            <p>
              You are not registered for this event yet.
            </p>

            <span className="participationStatus neutral">
              Available
            </span>
          </>
        ) : (
          <>
            <h3>
              {myRegistration.eventTitle || selectedEvent.title}
            </h3>

            <div className="participationDetails">
              <div>
                <strong>Status</strong>
                <span>{myRegistration.status || "Registered"}</span>
              </div>

              <div>
                <strong>Role</strong>
                <span>{myRegistration.attendeeType || "Participant"}</span>
              </div>

              <div>
                <strong>Checked In</strong>
                <span>{myRegistration.checkedIn ? "Yes" : "No"}</span>
              </div>
            </div>

            <span
              className={
                myRegistration.checkedIn
                  ? "participationStatus checked"
                  : "participationStatus registered"
              }
            >
              {myRegistration.checkedIn ? "Checked In" : "Registered"}
            </span>
          </>
        )}
      </section>
    );
  }
  return (
    <div className="hiringEventsPortal">
      <div className="eventsHeader">
        <div>
          <p className="eventsEyebrow">The iSpeakWell Project Job Bank</p>
          <h1>Hiring Events Portal</h1>
          <p>Create hiring events, manage attendees, track check-ins, and review outcomes.</p>
        </div>

        <button onClick={loadEvents}>Refresh</button>
      </div>

      <div className="eventsTabs">
        <button className={tab === "events" ? "active" : ""} onClick={() => setTab("events")}>
          Events
        </button>

        {canManageEvents && (
          <>
            <button className={tab === "attendees" ? "active" : ""} onClick={() => setTab("attendees")}>
              Attendees
            </button>

            <button className={tab === "checkin" ? "active" : ""} onClick={() => setTab("checkin")}>
              Check-In
            </button>

            <button className={tab === "reports" ? "active" : ""} onClick={() => setTab("reports")}>
              Reports
            </button>

            <button className={tab === "manage" ? "active" : ""} onClick={() => setTab("manage")}>
              Manage Events
            </button>
          </>
        )}
      </div>

      {tab === "events" && (
        <div className="eventsLayout">
          <div className="eventsListPanel">
            <h2>Upcoming Events</h2>

            {activeEvents.length === 0 ? (
              <div className="eventsEmpty">No active hiring events yet.</div>
            ) : (
              activeEvents.map(event => (
                <button
                  className={`eventListCard ${selectedEvent?.id === event.id ? "selected" : ""}`}
                  key={event.id}
                  onClick={() => selectEvent(event)}
                >
                  <strong>{event.title}</strong>
                  <span>{event.eventType}</span>
                  <small>{formatDateTime(event.startDateTime)}</small>
                </button>
              ))
            )}
          </div>

          <div className="eventDetailsPanel">
            {!selectedEvent ? (
              <div className="eventsEmpty">Select an event to view details.</div>
            ) : (
              <div>
                <EventSummary
                  event={selectedEvent}
                  stats={selectedStats}
                  formatDateTime={formatDateTime}
                  onEdit={() => editEvent(selectedEvent)}
                  onRemove={() => removeEvent(selectedEvent.id)}
                  canManageEvents={canManageEvents}
                />

                <div className="eventParticipationGrid">

                  <YourParticipationCard
                    selectedEvent={selectedEvent}
                    myRegistrations={myRegistrations}
                  />

                  <div className="registrationBox">
                    <h3>Register for this Event</h3>

                    <div className="registrationTypeButtons">
                      <button
                        className={attendeeType === "Candidate" ? "active" : ""}
                        onClick={() => setAttendeeType("Candidate")}
                      >
                        Candidate / Client
                      </button>

                      <button
                        className={attendeeType === "Employer" ? "active" : ""}
                        onClick={() => setAttendeeType("Employer")}
                      >
                        Employer
                      </button>
                    </div>

                    <div className="registrationGrid">
                      <input
                        value={registrationForm.firstName}
                        placeholder="First name"
                        onChange={(e) => updateRegistrationForm("firstName", e.target.value)}
                      />

                      <input
                        value={registrationForm.lastName}
                        placeholder="Last name"
                        onChange={(e) => updateRegistrationForm("lastName", e.target.value)}
                      />

                      <input
                        value={registrationForm.email}
                        placeholder="Email"
                        onChange={(e) => updateRegistrationForm("email", e.target.value)}
                      />

                      <input
                        value={registrationForm.phone}
                        placeholder="Phone"
                        onChange={(e) => updateRegistrationForm("phone", e.target.value)}
                      />

                      {attendeeType === "Employer" && (
                        <>
                          <input
                            value={registrationForm.organizationName}
                            placeholder="Organization name"
                            onChange={(e) =>
                              updateRegistrationForm("organizationName", e.target.value)
                            }
                          />

                          <input
                            value={registrationForm.positionTitle}
                            placeholder="Position title"
                            onChange={(e) =>
                              updateRegistrationForm("positionTitle", e.target.value)
                            }
                          />
                        </>
                      )}
                    </div>

                    <textarea
                      value={registrationForm.notes}
                      placeholder="Notes or accessibility needs"
                      onChange={(e) => updateRegistrationForm("notes", e.target.value)}
                    />

                    <button
                      className="registerEventButton"
                      onClick={() => registerForEvent(selectedEvent)}
                      disabled={registering}
                    >
                      {registering ? "Registering..." : "Register"}
                    </button>
                  </div>

                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "attendees" && canManageEvents && (
        <AttendeesView
          selectedEvent={selectedEvent}
          attendeeView={attendeeView}
          setAttendeeView={setAttendeeView}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          filteredRegistrations={filteredRegistrations}
          stats={selectedStats}
          toggleCheckIn={toggleCheckIn}
          savingRegistrationId={savingRegistrationId}
        />
      )}

      {tab === "checkin" && canManageEvents && (
        <CheckInView
          selectedEvent={selectedEvent}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          registrations={filteredRegistrations}
          setAttendeeView={setAttendeeView}
          toggleCheckIn={toggleCheckIn}
          savingRegistrationId={savingRegistrationId}
        />
      )}

      {tab === "reports" && canManageEvents && (
        <ReportsView selectedEvent={selectedEvent} stats={selectedStats} registrations={registrations} />
      )}

      {tab === "manage" && canManageEvents && (
        <div className="manageEventsLayout">
          <form className="eventFormPanel" onSubmit={saveEvent}>
            <h2>{editingEventId ? "Edit Hiring Event" : "Create Hiring Event"}</h2>

            <input value={eventForm.title} placeholder="Event title" required onChange={(e) => updateForm("title", e.target.value)} />

            <select value={eventForm.eventType} onChange={(e) => updateForm("eventType", e.target.value)}>
              <option>Employer Mixer</option>
              <option>Hiring Event</option>
              <option>Career Showcase</option>
              <option>Workshop</option>
              <option>Resource Fair</option>
            </select>

            <div className="eventFormGrid">
              <label>Start<input type="datetime-local" value={eventForm.startDateTime} onChange={(e) => updateForm("startDateTime", e.target.value)} /></label>
              <label>End<input type="datetime-local" value={eventForm.endDateTime} onChange={(e) => updateForm("endDateTime", e.target.value)} /></label>
            </div>

            <input value={eventForm.location} placeholder="Location" onChange={(e) => updateForm("location", e.target.value)} />
            <textarea value={eventForm.description} placeholder="Event description" onChange={(e) => updateForm("description", e.target.value)} />
            <textarea value={eventForm.targetSectors} placeholder="Target sectors — one per line" onChange={(e) => updateForm("targetSectors", e.target.value)} />

            <div className="eventFormGrid">
              <label>Employer Capacity<input type="number" min="0" value={eventForm.employerCapacity} onChange={(e) => updateForm("employerCapacity", e.target.value)} /></label>
              <label>Candidate Capacity<input type="number" min="0" value={eventForm.candidateCapacity} onChange={(e) => updateForm("candidateCapacity", e.target.value)} /></label>
            </div>

            <label className="eventCheckboxLabel">
              <input type="checkbox" checked={eventForm.active} onChange={(e) => updateForm("active", e.target.checked)} />
              Active event
            </label>

            <div className="eventFormActions">
              <button type="submit" disabled={savingEvent}>{savingEvent ? "Saving..." : "Save Event"}</button>
              <button type="button" onClick={clearEventForm}>Clear</button>
            </div>
          </form>

          <div className="existingEventsPanel">
            <h2>Existing Events</h2>

            {events.map(event => (
              <div className="existingEventCard" key={event.id}>
                <h3>{event.title}</h3>
                <p>{formatDateTime(event.startDateTime)}</p>
                <div>
                  <button onClick={() => editEvent(event)}>Edit</button>
                  <button onClick={() => removeEvent(event.id)}>Remove</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {createPortal(
        <GlobalNavDrawer
          hideCurrent
          links={[
            { to: "/purchase", label: `Tokens: ${balance}` },
            { to: "/jobbank", label: "Job Bank Home" },
            { to: "/voicebot", label: "iSpeakWell" },
            { to: "/resumerevisor", label: "Resumes" },
            { to: "/conversationpractice", label: "Convos" },
            { to: "/interview-practice", label: "Interviews" },
            { to: "/termsofservice", label: "Terms" },
            { to: "/privacypolicy", label: "Privacy" },
            { to: "/admin", label: "Admin" },
            { to: "/login", label: "Logout", onClick: async () => { await logout(); } }
          ]}
        />,
        document.body
      )}
    </div>
  );
}

function EventSummary({ event, stats, formatDateTime, canManageEvents, onEdit, onRemove }) {
  return (
    <>
      <div className="eventTitleRow">
        <div>
          <h2>{event.title}</h2>
          <p>{event.eventType}</p>
        </div>

        <span className="eventStatusPill">{event.active !== false ? "Active" : "Inactive"}</span>
      </div>

      <div className="eventMetaGrid">
        <div><strong>Starts</strong><p>{formatDateTime(event.startDateTime)}</p></div>
        <div><strong>Ends</strong><p>{formatDateTime(event.endDateTime)}</p></div>
        <div><strong>Location</strong><p>{event.location || "Not set"}</p></div>
        <div><strong>Capacity</strong><p>{event.employerCapacity || 0} employers / {event.candidateCapacity || 0} clients</p></div>
      </div>

      <section className="eventDescriptionBlock">
        <h3>Description</h3>
        <p>{event.description || "No description yet."}</p>
      </section>

      <section className="eventDescriptionBlock">
        <h3>Target Sectors</h3>
        <div className="sectorPills">
          {(event.targetSectors || []).length === 0 ? (
            <span>No sectors listed</span>
          ) : (
            event.targetSectors.map(sector => <span key={sector}>{sector}</span>)
          )}
        </div>
      </section>

      <div className="eventStatsGrid">
        <div><strong>{stats.total}</strong><span>Total</span></div>
        <div><strong>{stats.employers}</strong><span>Employers</span></div>
        <div><strong>{stats.candidates}</strong><span>Clients</span></div>
        <div><strong>{stats.checkedIn}</strong><span>Checked In</span></div>
      </div>

      {canManageEvents && (
        <div className="eventActions">
          <button onClick={onEdit}>Edit Event</button>
          <button onClick={onRemove}>Remove Event</button>
        </div>
      )}
    </>
  );
}

function AttendeesView({ selectedEvent, attendeeView, setAttendeeView, searchTerm, setSearchTerm, filteredRegistrations, stats, toggleCheckIn, savingRegistrationId }) {
  return (
    <div className="attendeesPanel">
      <div className="attendeesHeader">
        <div>
          <h2>Attendees{selectedEvent ? ` — ${selectedEvent.title}` : ""}</h2>
          <p>Manage registered candidates, employers, and event attendance.</p>
        </div>
      </div>

      <div className="attendeeFilterBar">
        <button className={attendeeView === "Candidate" ? "active" : ""} onClick={() => setAttendeeView("Candidate")}>Candidates ({stats.candidates})</button>
        <button className={attendeeView === "Employer" ? "active" : ""} onClick={() => setAttendeeView("Employer")}>Employers ({stats.employers})</button>
        <button className={attendeeView === "Checked In" ? "active" : ""} onClick={() => setAttendeeView("Checked In")}>Checked In ({stats.checkedIn})</button>
      </div>

      <input className="attendeeSearch" value={searchTerm} placeholder="Search attendees..." onChange={(e) => setSearchTerm(e.target.value)} />

      <div className="attendeeCardGrid">
        {filteredRegistrations.length === 0 ? (
          <div className="eventsEmpty">No matching attendees.</div>
        ) : (
          filteredRegistrations.map(registration => (
            <RegistrationCard key={registration.id} registration={registration} toggleCheckIn={toggleCheckIn} savingRegistrationId={savingRegistrationId} />
          ))
        )}
      </div>
    </div>
  );
}

function CheckInView({ selectedEvent, searchTerm, setSearchTerm, registrations, setAttendeeView, toggleCheckIn, savingRegistrationId }) {
  useEffect(() => {
    setAttendeeView("Candidate");
  }, [setAttendeeView]);

  return (
    <div className="checkInPanel">
      <h2>Live Check-In{selectedEvent ? ` — ${selectedEvent.title}` : ""}</h2>
      <input className="attendeeSearch" value={searchTerm} placeholder="Search name, email, phone, or organization..." onChange={(e) => setSearchTerm(e.target.value)} />

      <div className="checkInList">
        {registrations.length === 0 ? (
          <div className="eventsEmpty">No attendees found.</div>
        ) : (
          registrations.map(registration => (
            <RegistrationCard key={registration.id} registration={registration} toggleCheckIn={toggleCheckIn} savingRegistrationId={savingRegistrationId} checkInMode />
          ))
        )}
      </div>
    </div>
  );
}

function RegistrationCard({ registration, toggleCheckIn, savingRegistrationId, checkInMode = false }) {
  const name = getRegistrationName(registration);

  return (
    <article className={`attendeeCard ${registration.checkedIn ? "checkedIn" : ""}`}>
      <div className="attendeeCardHeader">
        <div>
          <h3>{name}</h3>
          <p>{registration.attendeeType}</p>
        </div>

        <span className={registration.checkedIn ? "checkedInPill" : "registeredPill"}>
          {registration.checkedIn ? "Checked In" : registration.status || "Registered"}
        </span>
      </div>

      <div className="attendeeDetails">
        <div><strong>Email</strong><span>{registration.email || "Not provided"}</span></div>
        <div><strong>Phone</strong><span>{registration.phone || "Not provided"}</span></div>

        {registration.attendeeType === "Employer" && (
          <>
            <div><strong>Organization</strong><span>{registration.organizationName || "Not provided"}</span></div>
            <div><strong>Position</strong><span>{registration.positionTitle || "Not provided"}</span></div>
          </>
        )}

        {registration.notes && (
          <div className="attendeeNotes"><strong>Notes</strong><span>{registration.notes}</span></div>
        )}
      </div>

      <button
        className={checkInMode ? "bigCheckInButton" : ""}
        onClick={() => toggleCheckIn(registration)}
        disabled={savingRegistrationId === registration.id}
      >
        {registration.checkedIn ? "Undo Check-In" : "Check In"}
      </button>
    </article>
  );
}

function ReportsView({ selectedEvent, stats, registrations }) {
  const attendanceRate = stats.total ? Math.round((stats.checkedIn / stats.total) * 100) : 0;

  return (
    <div className="reportsPanel">
      <h2>Reports{selectedEvent ? ` — ${selectedEvent.title}` : ""}</h2>

      <div className="reportStatsGrid">
        <div><strong>{stats.total}</strong><span>Registered</span></div>
        <div><strong>{stats.checkedIn}</strong><span>Checked In</span></div>
        <div><strong>{attendanceRate}%</strong><span>Attendance Rate</span></div>
        <div><strong>{stats.notCheckedIn}</strong><span>Not Checked In</span></div>
      </div>

      <section className="eventDescriptionBlock">
        <h3>Attendee Breakdown</h3>
        <p>{stats.candidates} candidates and {stats.employers} employers are registered for this event.</p>
      </section>

      <section className="eventDescriptionBlock">
        <h3>Data Quality</h3>
        <p>
          {registrations.filter(reg => getRegistrationName(reg).startsWith("Unnamed")).length} registrations need name cleanup.
        </p>
      </section>
    </div>
  );
}